import { ColDef, ExcelDataType, ExcelRow } from "ag-grid-community";
import { AgGridFunctions } from "./ag-grid-functions";
import { AgGridAngular } from "ag-grid-angular";
import { formatDate } from "@angular/common";
import { AuditAction } from "../models/audit-action";
import { LoggingService } from "../services/api/logging.service";
import { ExportService } from "../services/files/export.service";
import { inject, QueryList } from "@angular/core";
import { AgChartInstance, AgCharts } from "ag-charts-enterprise";
import { firstValueFrom } from "rxjs";

export interface IQueryFilterRow {
  label?: string;
  value?: string | null;
  type?: "String" | "Number" | "Boolean" | "DateTime";
}

export type ExportNumberFormat = "number" | "percent";
export type ExportPercentScale = "whole" | "fraction";

// Export hints live in colDef.context; AG Grid 36 warns (#307) about unknown top-level colDef properties.
export interface IExportColContext {
  exportNumberFormat?: ExportNumberFormat;
  exportPercentScale?: ExportPercentScale;
}

export interface IExportableColDef<TData = unknown, TValue = unknown>
  extends ColDef<TData, TValue> {
  context?: IExportColContext;
}

type ExportGrid = AgGridAngular<unknown, ColDef<unknown, unknown>>;
type ExportGridInput = ExportGrid | QueryList<ExportGrid>;
type ExportSheetNameResolver = (grid: ExportGrid, index: number) => string;
export type ExportAppendContentResolver = (
  grid?: ExportGrid,
  index?: number
) => ExcelRow[] | undefined;
export type ExportRowGroupResolver = (params: {
  node?: {
    key?: string | null;
    allLeafChildren?: Array<{ data?: unknown }>;
  } | null;
}) => string;

export class AgGridExportHelper {
  loggingService: LoggingService = inject(LoggingService);
  exportService: ExportService = inject(ExportService);

  async onExport(
    filename?: string,
    firstSheetName?: string,
    filters?: IQueryFilterRow[],
    agGrid?: ExportGridInput,
    excludeColumnIds: string[] = [],
    sheetNameResolver?: ExportSheetNameResolver,
    appendContentResolver?: ExportAppendContentResolver,
    rowGroupResolver?: ExportRowGroupResolver
  ) {
    filename = filename
      ? `${filename}_${formatDate(Date.now(), "yyyy-MM-dd-HH-mm-ss-SSS", "en")}`
      : (() => {
          throw new Error("Filename is required for export.");
        })();
    const grids = this.resolveExportGrids(agGrid);
    if (grids.length === 0) {
      throw new Error("AG Grid instance is required for export.");
    }

    const firstGrid = grids[0];

    const excludedColumns = new Set(excludeColumnIds);
    const exportSheets = grids
      .map((grid, index) => {
        const sheetName = this.getSheetName(
          grid,
          index,
          firstSheetName,
          sheetNameResolver
        );

        const exportColumnKeys = (grid?.api?.getColumns() ?? [])
          .map((column) => column.getColId())
          .filter((key): key is string => !!key && !excludedColumns.has(key));
        const hasExcludedColumns = excludedColumns.size > 0;

        return grid?.api?.getSheetDataForExcel({
          sheetName,
          columnKeys:
            hasExcludedColumns && exportColumnKeys.length > 0
              ? exportColumnKeys
              : undefined,
          appendContent: appendContentResolver?.(grid, index),
          processRowGroupCallback: rowGroupResolver
            ? (params) => rowGroupResolver(params)
            : undefined,
          processCellCallback: (params) =>
            AgGridExportHelper.getExportCellValue(params) as string,
          columnWidth: (params) => {
            const colId = params.column?.getColId();
            return colId
              ? AgGridFunctions.getExportColumnWidth(grid.api, colId)
              : 120;
          },
        });
      })
      .filter((sheet): sheet is string => !!sheet);

    const queryFilterContent = this.getQueryFiltersSheetContent(filters ?? []);
    const sheets = [
      ...exportSheets,
      AgGridFunctions.getCustomContentOnlySheetData(
        firstGrid?.api,
        "Query-Filters",
        queryFilterContent
      ),
    ].filter(Boolean) as string[];

    const rowCount = grids.reduce(
      (total, grid) => total + this.getGridRowCount(grid),
      0
    );

    // // Export the report using AG Grid's API
    // this.agGrid?.api?.exportMultipleSheetsAsExcel({
    //   data: sheets,
    //   fileName: filename
    // });

    // Instead of using AG Grid's built-in export...
    // 1. generate the Excel workbook as a Blob,
    // 2. if blob exists, send API request for upload to blob storage,
    // 3. trigger browser download.
    const blob = firstGrid?.api?.getMultipleSheetsAsExcel({
      data: sheets,
      fileName: filename,
    });

    if (!blob) {
      throw new Error("Failed to generate Excel blob for export.");
    }

    await firstValueFrom(this.exportService.uploadThenDownloadReport(blob, filename, rowCount));

    const reportName = this.getSheetName(
      firstGrid,
      0,
      firstSheetName,
      sheetNameResolver
    );

    const reportJSON = JSON.stringify({
      ReportName: reportName,
      Filename: filename,
    });
    await firstValueFrom(
      this.loggingService.logAudit(
        AuditAction.ReportExported,
        `Report Name: ${reportName}, Filename: ${filename}`,
        reportJSON
      )
    );
  }

  async onExportChart(instance?: AgChartInstance, filename?: string, title?: string) {
      if (!instance) {
        throw new Error("Chart instance is required for export.");
      }

      if (!filename) {
        throw new Error("Filename is required for export.");
      }

      if (!title) {
        throw new Error("Chart title is required for export.");
      }

      
      const imageDataUrl = await AgCharts.getImageDataURL(instance);
      const imageBlob = await fetch(imageDataUrl).then(response => response.blob());

      if (!imageBlob) {
        throw new Error("Failed to generate image blob for export.");
      }

      await firstValueFrom(this.exportService.uploadThenDownloadReport(imageBlob, filename, 0, 'png'));
    
      const reportJSON = JSON.stringify({ReportName: title, Filename: filename});

      if (!reportJSON) {
        throw new Error("Failed to generate report JSON for export.");
      }

      await firstValueFrom(this.loggingService.logAudit(
        AuditAction.ReportExported,
        `Report Name: ${title}, Filename: ${filename}`,
        reportJSON
      ));
  }

  private resolveExportGrids(agGrid?: ExportGridInput): ExportGrid[] {
    if (!agGrid) {
      return [];
    }

    if (agGrid instanceof QueryList) {
      return agGrid.toArray().filter((grid): grid is ExportGrid => !!grid?.api);
    }

    return agGrid?.api ? [agGrid] : [];
  }

  private getSheetName(
    grid: ExportGrid,
    index: number,
    firstSheetName?: string,
    sheetNameResolver?: ExportSheetNameResolver
  ): string {
    if (sheetNameResolver) {
      return sheetNameResolver(grid, index);
    }

    if (index === 0) {
      return firstSheetName ?? "Sheet1";
    }

    return grid.api?.getGridId?.() ?? `Sheet${index + 1}`;
  }

  private getGridRowCount(agGrid: ExportGrid): number {
    const rowData = (agGrid as { rowData?: unknown[] })?.rowData;
    if (Array.isArray(rowData)) {
      return rowData.length;
    }

    return agGrid?.api?.getDisplayedRowCount?.() ?? 0;
  }

  static getExportCellValue(params: {
    column: { getColDef(): ColDef<unknown, unknown> };
    value: unknown;
    node?: { group?: boolean } | null;
    formatValue?: (value: unknown) => string | null | undefined;
  }) {
    const colDef = params.column.getColDef() as IExportableColDef;
    const exportContext = colDef.context;
    const isGroupedRow = !!params.node?.group;
    const numericValue = Number(params.value ?? 0);

    // App-wide sentinel for "no value" in numeric metrics.
    if (
       numericValue === -1 &&
       (colDef.type === "numericColumn" || exportContext?.exportNumberFormat === "percent")
     ) {
      return "";
    }

    if (
      isGroupedRow &&
      (colDef.type === "numericColumn" || exportContext?.exportNumberFormat === "percent")
    ) {
      return "";
    }

    if (exportContext?.exportNumberFormat === "percent") {
      if (!Number.isFinite(numericValue)) {
        return 0;
      }

      return exportContext?.exportPercentScale === "whole"
        ? numericValue / 100
        : numericValue;
    }

    if (colDef.type === "numericColumn") {
      return Number.isFinite(numericValue) ? numericValue : 0;
    }

    if (typeof params.formatValue === "function") {
      try {
        const formattedValue = params.formatValue(params.value);
        if (formattedValue !== null && formattedValue !== undefined) {
          return formattedValue;
        }
      } catch {
        return params.value;
      }
    }

    return params.value;
  }

  private getQueryFiltersSheetContent(filters: IQueryFilterRow[]): ExcelRow[] {
    if (filters.length === 0) {
      return [
        {
          cells: [
            {
              data: {
                value: "No query filters applied",
                type: "String",
              },
            },
            {
              data: {
                value: "",
                type: "String",
              },
            },
          ],
        },
      ];
    }

    const rows: ExcelRow[] = [];
    filters.forEach((filter) => {
      const row: ExcelRow = {
        cells: [
          {
            data: {
              value: filter.label ?? "",
              type: "String",
            },
          },
          {
            data: {
              value: filter.value ?? "",
              type: (filter.type as ExcelDataType) ?? "String",
            },
          },
        ],
      };
      rows.push(row);
    });

    return rows;
  }
}
